<template>
  <div>
    <v-text-field
      v-model="donationAmount"
      data-cy="donation-amount-text-field"
      :validate-on-blur="true"
      hide-label
      :placeholder="t('donationDetails.form.donationAmount.placeholder')"
      :hint="t('donationDetails.form.donationAmount.hint')"
      :suffix="currentCurrency"
      :readonly="readOnly"
      type="number"
      :min="min"
      :max="max"
      :rules="rules"
      density="comfortable"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted } from 'vue';
import i18n from '@/plugins/i18n';
import { useLabel } from '@/composables/useLabel';
import { getCurrencyForLocation } from '@/utils/currency';
import store from '@/store';

export default defineComponent({
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    modelValue: {
      type: [String, Number],
      default: undefined,
    },
    max: {
      type: Number,
      default: null,
    },
    min: {
      type: Number,
      default: null,
    },
    adminFee: {
      type: Number,
      default: null,
    },
    rules: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    displayFee: {
      type: Boolean,
      default: true,
    },
    hideLabel: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input', 'update:modelValue'],
  setup(props, { emit }) {
    const { t: localT } = useLabel();
    const t = (key: string, params?) => {
      try {
        const res = localT ? localT(key, params) : undefined;
        if (res !== undefined) return res;
      } catch {
        // fall-through to global translations
      }
      const globalT =
        i18n &&
        (i18n as any).vueI18n &&
        (i18n as any).vueI18n.global &&
        (i18n as any).vueI18n.global.t;
      if (typeof globalT === 'function') return globalT(key, params);
      return key;
    };
    const n = (value: number, options) => {
      const globalN =
        i18n &&
        (i18n as any).vueI18n &&
        (i18n as any).vueI18n.global &&
        (i18n as any).vueI18n.global.n;
      if (typeof globalN === 'function') return globalN(value, options);
      return String(value);
    };

    // prefer modelValue when provided (Vue 3 v-model). Fall back to legacy 'value' prop
    const donationAmount = ref(props.modelValue !== undefined ? props.modelValue : props.value);
    const hideFee = ref(true);

    const currentCurrency = computed(() => getCurrencyForLocation(store.state.userLocation || ''));
    const totalAmountCalculated = computed(() => {
      const amount: number = parseFloat(String(donationAmount.value));
      if (props.min <= amount && amount <= props.max) {
        return amount + props.adminFee;
      }
      return 0;
    });

    watch(donationAmount, (value) => {
      // Ensure we emit a numeric value so parent models receive a number (not a string)
      const parsed =
        value === '' || value === null || typeof value === 'undefined'
          ? null
          : parseInt(String(value));
      emit('input', parsed);
      // Support Vue 3 v-model consumers
      emit('update:modelValue', parsed);
    });
    watch(
      () => props.adminFee,
      () => {
        hideFee.value = !props.adminFee || props.adminFee === 0 || !props.displayFee;
      }
    );
    watch(
      () => props.displayFee,
      () => {
        hideFee.value = !props.adminFee || props.adminFee === 0 || !props.displayFee;
      }
    );
    watch(
      () => props.value,
      (newVal) => {
        // Only reflect legacy 'value' when modelValue isn't provided
        if (props.modelValue === undefined) donationAmount.value = newVal;
      }
    );

    watch(
      () => props.modelValue,
      (newVal) => {
        if (newVal !== undefined) donationAmount.value = newVal;
      }
    );

    onMounted(() => {
      hideFee.value = !props.adminFee || props.adminFee === 0 || !props.displayFee;
    });

    return {
      t,
      n,
      donationAmount,
      hideFee,
      currentCurrency,
      totalAmountCalculated,
    };
  },
});
</script>

<style>
.v-text-field__suffix {
  opacity: 1 !important;
}
</style>
